import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { EntitySupportingCard } from "@mapmycustomers/shared/types/entity";
import { EntityPin } from "@mapmycustomers/shared/types/map";
import { Alert } from "@mapmycustomers/ui";

import EmailSubscriptionStatusTag from "@app/component/EmailSubscriptionStatusTag";
import FieldGrid from "@app/component/FieldGrid";
import { EntityItemProperties, Item } from "@app/component/RecordCard";
import EntityActivities from "@app/component/RecordCard/components/EntityActivities";
import EntityAddress from "@app/component/RecordCard/components/EntityAddress";
import RecordActionsBar from "@app/component/RecordCard/components/RecordActionsBar";
import useRecordStylingContext from "@app/component/RecordCard/utils/useRecordStylingContext";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import styles from "./EntityCard.module.scss";

type EntityCardProps = {
  borderColor?: AnyColor | null;
  className?: string;
  extraActions?: React.ReactElement;
  isHighlighted?: boolean;
  isRemoveActionVisible?: boolean;
  onClick?: (entity: EntitySupportingCard) => void;
  onHover?: (entity: EntitySupportingCard) => void;
  onLeave?: (entity: EntitySupportingCard) => void;
  onRemove?: (entity: EntitySupportingCard) => void;
  primary?: boolean;
  removeActionConfirmationText?: string;
  removeActionHoverText?: string;
  showActions?: boolean;
  showDistance?: boolean;
  showPinnedFields?: boolean;
} & EntityItemProperties<EntitySupportingCard> &
  ReturnType<typeof mapStateToProps>;

const EntityCard: React.FC<EntityCardProps> = ({
  borderColor,
  className,
  columns,
  entity,
  extraActions,
  isHighlighted,
  isRemoveActionVisible,
  onClick,
  onEmailSubscribe,
  onEmailUnsubscribe,
  onHover,
  onLeave,
  onRemove,
  overrideFieldVisibility,
  primary,
  removeActionConfirmationText,
  removeActionHoverText,
  showActions = true,
  showDistance = false,
  showPinnedFields = true,
}) => {
  const intl = useIntl();
  const hasViewAccess = entity?.accessStatus?.read !== false;
  const enableGeneric = columns.actions && hasViewAccess;
  const showEngagement = columns.engagement && hasViewAccess;

  const handleHover = useCallback(() => {
    onHover?.(entity);
  }, [entity, onHover]);

  const handleLeave = useCallback(() => {
    onLeave?.(entity);
  }, [entity, onLeave]);

  const handleClick = useCallback(() => {
    onClick?.(entity);
  }, [entity, onClick]);

  const handleRemoveClick = useCallback(() => onRemove?.(entity), [entity, onRemove]);

  const recordStyling = useRecordStylingContext();
  const recordStyles = useMemo(
    // this is a dirty dirty hack, my bad
    // but changing defaultEntityStyleGetter to support normal records instead of EntityPins
    // requires extra work, but what's worse, I don't have a straight idea of how to
    // do that exactly. Because, strictly speaking defaultEntityStyleGetter should accept
    // EntityPins. Probably there must be some style getter that accepts entities and
    // defaultEntityStyleGetter will wrap it.
    // FIXME: address that issue later ^^^
    () => recordStyling.getPinStyle({ data: entity } as unknown as EntityPin),
    [entity, recordStyling]
  );

  const entityColumns = useMemo(() => {
    if (!hasViewAccess) {
      return [];
    }
    const originalColumns = columns[entity.entity];
    const defaultColumnsNames = new Set(originalColumns.map(({ name }) => name));
    const overriddenColumns = overrideFieldVisibility
      ? getFieldModelByEntityType(entity.entity).fields.filter(
          (field) =>
            !defaultColumnsNames.has(field.name) && overrideFieldVisibility?.(field, entity.entity)
        )
      : [];
    const result = overriddenColumns.length
      ? [...originalColumns, ...overriddenColumns]
      : originalColumns;
    return result;
  }, [columns, entity.entity, hasViewAccess, overrideFieldVisibility]);

  return (
    <Item
      actionsBar={
        hasViewAccess && (
          <RecordActionsBar
            className={styles.actionBar}
            enableGeneric={showActions && enableGeneric}
            entity={entity}
            extraActions={extraActions}
            onEdit={handleClick}
            onEmailSubscribe={onEmailSubscribe}
            onEmailUnsubscribe={onEmailUnsubscribe}
          />
        )
      }
      borderColor={
        borderColor ?? (entity.entity === EntityType.DEAL ? entity.stage.color : entity.color)
      }
      className={className}
      enableRemoveAction={isRemoveActionVisible}
      entity={entity}
      highlight={isHighlighted}
      icon={
        recordStyles?.icon && (recordStyles.icon as google.maps.Icon).url ? (
          <div className={styles.iconContainer}>
            <img alt="record icon" src={(recordStyles.icon as google.maps.Icon).url} />
          </div>
        ) : undefined
      }
      onClick={hasViewAccess ? handleClick : undefined}
      onHover={handleHover}
      onLeave={handleLeave}
      onRemoveAction={handleRemoveClick}
      removeActionConfirmationText={removeActionConfirmationText}
      removeActionHoverText={removeActionHoverText}
      showDistance={showDistance}
    >
      <EntityAddress entity={entity} popoverPlacement="topRight" />
      <div className={styles.tagsContainer}>
        {primary && (
          <Tag
            className={styles.tag}
            icon={<FontAwesomeIcon className={styles.star} icon={faStar} />}
          >
            {intl.formatMessage({
              id: "recordCard.primaryRecord",
              defaultMessage: "Primary",
              description: "Primary Record Tag",
            })}
          </Tag>
        )}
        {(entity.entity === EntityType.COMPANY || entity.entity === EntityType.PERSON) && (
          <EmailSubscriptionStatusTag emailSubscribed={entity.subscribed} />
        )}
        {showEngagement && <EntityActivities entity={entity} />}
      </div>
      {hasViewAccess && showPinnedFields && (
        <FieldGrid columns={entityColumns} entity={entity} showEmpty />
      )}
      {!hasViewAccess && (
        <Alert
          message={intl.formatMessage({
            id: "recordCard.noAccess",
            defaultMessage:
              "You do not have permission to view or edit this record. Contact your administrator for access.",
            description: "Warning message to show on record item if it has no view access",
          })}
          showIcon
          type="warning"
        />
      )}
    </Item>
  );
};

const mapStateToProps = (state: RootState) => ({
  columns: getRecordPreviewConfigurationColumns(state),
});

export default connect(mapStateToProps)(EntityCard);
